import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle, Copy, ChevronDown, ChevronRight, Terminal } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * "Enable server backups" panel for the Settings page.
 *
 * Uses GET /api/system/backup/setup-check to render a live checklist of
 * prerequisites (mongodump installed? backup dir writable?) and shows the
 * exact shell commands / cron line the user should run on their Ubuntu
 * server. Every command block has a Copy button.
 */
function CopyBlock({ label, command, testid }) {
  const copy = () => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(command)
        .then(() => toast.success("Copied"))
        .catch(() => toast.error("Copy failed — please select manually"));
    } else {
      toast.error("Clipboard not available in this browser");
    }
  };
  return (
    <div className="bg-slate-900 rounded-md overflow-hidden">
      {label && (
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-800">
          {label}
        </div>
      )}
      <div className="flex items-start gap-2 px-3 py-2">
        <Terminal className="size-3.5 text-emerald-400 shrink-0 mt-0.5" />
        <pre
          data-testid={testid ? `${testid}-cmd` : undefined}
          className="flex-1 text-xs text-emerald-100 whitespace-pre-wrap break-all font-mono leading-relaxed"
        >
          {command}
        </pre>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid={testid ? `${testid}-copy` : undefined}
          className="h-7 w-7 p-0 text-slate-300 hover:text-white hover:bg-slate-800 shrink-0"
          onClick={copy}
          title="Copy to clipboard"
        >
          <Copy className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

function CheckItem({ ok, label, hint }) {
  return (
    <div className="flex items-start gap-2" data-testid={`backup-check-${ok ? "ok" : "fail"}`}>
      {ok ? (
        <CheckCircle2 className="size-4 text-emerald-600 shrink-0 mt-0.5" />
      ) : (
        <XCircle className="size-4 text-red-600 shrink-0 mt-0.5" />
      )}
      <div className="text-sm">
        <span className={ok ? "text-emerald-900" : "text-red-900"}>{label}</span>
        {hint && <span className="text-muted-foreground ml-2 text-xs">— {hint}</span>}
      </div>
    </div>
  );
}

export function BackupSetupPanel() {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["backup-setup-check"],
    queryFn: () => api.getBackupSetupCheck(),
  });

  const ready = !!data?.ready;
  const checks = data?.checks || {};
  const c = data?.commands || {};

  return (
    <section
      data-testid="backup-setup-panel"
      className="border border-border rounded-md bg-white"
    >
      <button
        type="button"
        data-testid="backup-setup-toggle"
        className="w-full flex items-center gap-3 p-5 text-left hover:bg-slate-50/50 transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? (
          <ChevronDown className="size-4 text-slate-500 shrink-0" />
        ) : (
          <ChevronRight className="size-4 text-slate-500 shrink-0" />
        )}
        <div className="flex-1">
          <h3 className="font-display font-semibold text-lg">
            How to enable server backups on your Ubuntu server
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading
              ? "Checking server readiness…"
              : ready
                ? "Everything looks ready — just add the cron line below."
                : "One or more prerequisites are missing. Follow the steps below to enable automatic daily backups."}
          </p>
        </div>
        <span
          data-testid="backup-setup-status-pill"
          className={`text-xs px-2 py-1 rounded-full font-medium ${
            ready
              ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
              : "bg-amber-50 text-amber-800 ring-1 ring-amber-200"
          }`}
        >
          {isLoading ? "…" : ready ? "Ready" : "Setup needed"}
        </span>
      </button>

      {expanded && !isLoading && (
        <div className="border-t border-border p-5 space-y-4">
          {/* ---- Live checks ---- */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 bg-slate-50/50 border border-slate-200 rounded-md p-3">
            <CheckItem
              ok={checks.mongodump_installed}
              label="mongodump installed"
              hint={
                checks.mongodump_installed
                  ? checks.mongodump_path
                  : "Run step 1 below"
              }
            />
            <CheckItem
              ok={checks.mongorestore_installed}
              label="mongorestore installed"
              hint={
                checks.mongorestore_installed ? "" : "Included in the mongo tools package"
              }
            />
            <CheckItem
              ok={checks.backup_dir_exists}
              label={`Backup directory exists (${checks.backup_dir || "/var/backups/kinos"})`}
              hint={checks.backup_dir_exists ? "" : "Run step 2 below"}
            />
            <CheckItem
              ok={checks.backup_dir_writable}
              label="Backup directory is writable"
              hint={checks.backup_dir_writable ? "" : "Fix ownership: chown \"$USER\":\"$USER\""}
            />
          </div>

          {data?.last_backup_at && (
            <div className="text-xs text-muted-foreground">
              Last scheduled backup: <span className="mono">{new Date(data.last_backup_at).toLocaleString()}</span>
            </div>
          )}

          {/* ---- Setup steps ---- */}
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <span className="inline-flex items-center justify-center size-5 rounded-full bg-primary text-white text-xs font-bold">1</span>
                Install MongoDB tools
              </h4>
              <p className="text-xs text-muted-foreground mb-1.5 ml-7">
                Adds <code className="mono">mongodump</code> and <code className="mono">mongorestore</code> — required by the backup script.
              </p>
              <CopyBlock command={c.install_tools} testid="backup-setup-step1" />
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <span className="inline-flex items-center justify-center size-5 rounded-full bg-primary text-white text-xs font-bold">2</span>
                Create the backup directory
              </h4>
              <p className="text-xs text-muted-foreground mb-1.5 ml-7">
                Cron will write daily <code className="mono">.tar.gz</code> files here. Retention is 7 daily + 4 weekly.
              </p>
              <CopyBlock command={c.create_dir} testid="backup-setup-step2" />
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <span className="inline-flex items-center justify-center size-5 rounded-full bg-primary text-white text-xs font-bold">3</span>
                Run a one-off backup to confirm
              </h4>
              <p className="text-xs text-muted-foreground mb-1.5 ml-7">
                Should print the path of a new <code className="mono">.tar.gz</code>. If this succeeds, add the cron line in step 4.
              </p>
              <CopyBlock command={c.run_once} testid="backup-setup-step3" />
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <span className="inline-flex items-center justify-center size-5 rounded-full bg-primary text-white text-xs font-bold">4</span>
                Add the daily cron line
              </h4>
              <p className="text-xs text-muted-foreground mb-1.5 ml-7">
                Runs every day at <span className="mono">02:00</span> server time. Paste this into <code className="mono">crontab -e</code>.
              </p>
              <CopyBlock command={data?.cron_line || ""} testid="backup-setup-step4" />
            </div>
          </div>

          <div className="text-xs text-muted-foreground border-t border-border pt-3">
            Full docs (weekly digest, software reminders, off-host copies):{" "}
            <code className="mono text-[11px]">/app/backend/scripts/README_BACKUP.md</code>
          </div>
        </div>
      )}
    </section>
  );
}
